<template>
    <div v-if="invites.length > 0" class="min-w-50 flex flex-col gap-4">
        <h3 class="scaffold-body text-valence-grey-850">
            <i class="text-lg/none mr-1 bi bi-people" aria-hidden="true"></i>
            360 Feedback
        </h3>
        <div class="rounded-xl overflow-hidden border bg-linear-to-r from-[#886ba2] via-[#645abe] via-30% to-[#632ead] text-white p-6">
            <div class="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div class="flex-1">
                    <h3 class="title-body">{{ cardTitle }}</h3>
                    <p class="mt-3 body-small">{{ cardSubtitle }}</p>
                </div>

                <div class="flex w-full flex-col gap-3 md:w-auto md:min-w-65">
                    <a
                        v-for="invite in invites"
                        :key="invite.invite_token"
                        :href="`/reflections/360/r/${invite.invite_token}/`"
                        class="group flex items-center justify-between gap-3 rounded-2xl px-5 py-4 text-left transition-colors duration-200 bg-white/10 text-white hover:bg-white/15"
                        @click="logCtaClick(invite)">
                        <span class="flex items-center gap-1 md:gap-3">
                            <BaseAvatar size="sm" shape="circle" class="bg-white/20 text-white"><i class="bi bi-clock" aria-hidden="true" /></BaseAvatar>
                            <span class="details md:body-small text-white"> Give feedback </span>
                        </span>
                        <i class="bi bi-arrow-right text-white/80 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-white" aria-hidden="true"></i>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import type { PendingRaterInvite } from "~vue/types/chat_home";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted } from "vue";

interface Props {
    invites: PendingRaterInvite[];
}

const props = withDefaults(defineProps<Props>(), {
    invites: () => [],
});

const cardTitle = computed(() => {
    if (props.invites.length === 1) {
        return `${props.invites[0].subject_first_name} has invited you to give them feedback`;
    }
    return "You've been invited to give feedback";
});

const cardSubtitle = computed(() => {
    if (props.invites.length === 1) {
        return "Answer a few short questions to help them grow. Your responses are anonymous.";
    }
    return `You have ${props.invites.length} pending 360 feedback requests. Your responses are anonymous.`;
});

onMounted(() => {
    if (props.invites.length > 0) {
        logUserInteraction(AmplitudeEventNames.RATER_ASSESSMENT_CARD_SHOWN, { invite_count: props.invites.length });
    }
});

const logCtaClick = (invite: PendingRaterInvite) => {
    logUserInteraction(AmplitudeEventNames.RATER_ASSESSMENT_CARD_CTA_CLICKED, {
        subject_first_name: invite.subject_first_name,
        invite_count: props.invites.length,
    });
};
</script>
